import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TerrainIcon from "@mui/icons-material/Terrain";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { decryptSecretKey, dateTimeFormat } from "services/utils";
import {
	load_course_terrain,
	load_course_elements,
	upload_course_elevation,
} from "redux/HeatMapSlice";
import { datasetKey, putDataset, getEdited } from "services/heatmapIdb";
import useNotiStack from "hooks/useNotistack";

// Turn a raw API/envelope error into a friendly, resolution-aware message.
const friendlyError = (raw, step) => {
	const m = (raw || "").toLowerCase();
	if (m.includes("permission"))
		return `This account doesn't have access to ${step}m terrain data yet.`;
	if (m.includes("not found"))
		return `No ${step}m terrain is available for this course.`;
	if (m.includes("elevationarray"))
		return `The ${step}m terrain response was empty or malformed.`;
	return raw || `Couldn't load ${step}m terrain for this course.`;
};

// Embedded terrain editor page. Fetches terrain + elements for a course at the
// chosen resolution, stores them in IndexedDB, then renders Josh's Three.js tool
// in an iframe pointed at that IndexedDB record. Save flows back via postMessage.
const HeatMapEditor = () => {
	const [params] = useSearchParams();
	const id_course = params.get("range");
	const step = Number(params.get("step")) || 1;
	const id_courseInt = params.get("courseInt");

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { toastError, toastSuccess } = useNotiStack();

	const [status, setStatus] = useState("loading"); // loading | ready | error
	const [message, setMessage] = useState("");
	const [iframeKey, setIframeKey] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [lastModified, setLastModified] = useState(null);

	const secret_Key = decryptSecretKey();
	const profileDetails = JSON.parse(localStorage.getItem("profile_data"));

	useEffect(() => {
		let cancelled = false;
		const user = { secretKey: secret_Key, userName: profileDetails?.username };

		const load = async () => {
			if (!id_course) {
				setStatus("error");
				setMessage("Missing course id.");
				return;
			}
			try {
				const terrainRes = await dispatch(
					load_course_terrain({ id_course, step, user })
				).unwrap();
				const terrain = terrainRes?.terrain;

				if (!terrain?.elevationArray) {
					throw new Error("Terrain response had no elevationArray.");
				}

				// Elements are optional — terrain still renders without an overlay.
				let elements = null;
				let elementsModified = null;
				try {
					const elRes = await dispatch(
						load_course_elements({ id_course, user })
					).unwrap();
					if (elRes?.elements?.vectorGPSObject) elements = elRes.elements;
					elementsModified = elRes?.dateModified || null;
				} catch (_) {
					// no elements for this course; continue terrain-only
				}

				if (cancelled) return;
				setLastModified(terrainRes?.dateModified || elementsModified || null);

				const key = datasetKey(id_course, step);
				await putDataset({
					key,
					stem: id_course,
					step,
					id_course,
					id_courseInt: id_courseInt ? Number(id_courseInt) : undefined,
					terrain,
					elements,
				});

				if (cancelled) return;
				setIframeKey(key);
				setStatus("ready");
			} catch (err) {
				if (cancelled) return;
				setStatus("error");
				setMessage(friendlyError(err?.message, step));
			}
		};

		load();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id_course, step, id_courseInt]);

	// Receive the Save signal from the iframed tool. The edited JSON is in
	// IndexedDB (store "edits"); the upload to CourseElevationDataCreate is wired
	// in the next phase (PR3).
	const onMessage = useCallback(
		async (event) => {
			if (event.origin !== window.location.origin) return;
			const data = event?.data;
			if (!data || data.type !== "heatmap-save") return;
			try {
				const edited = await getEdited(data.key);
				if (!edited?.text) {
					toastError("Could not read edited terrain.");
					return;
				}
				if (!id_courseInt) {
					toastError("Missing course id — cannot upload this terrain.");
					return;
				}
				setUploading(true);
				await dispatch(
					upload_course_elevation({
						id_courseInt,
						step,
						jsonText: edited.text,
						user: {
							secretKey: secret_Key,
							userName: profileDetails?.username,
						},
					})
				).unwrap();
				toastSuccess("Terrain uploaded successfully.");
			} catch (e) {
				toastError(e?.message || "Upload failed.");
			} finally {
				setUploading(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[toastError, toastSuccess, dispatch, id_courseInt, step]
	);

	useEffect(() => {
		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, [onMessage]);

	const goBack = () => navigate("/heatmap");

	// A slim header bar so the user can always get back out of the iframe, and to
	// show which course + resolution is being edited.
	const TopBar = () => (
		<Box
			sx={{
				position: "relative",
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				px: 2,
				height: 52,
				bgcolor: "var(--blue)",
				color: "var(--white)",
				flex: "0 0 auto",
			}}
		>
			<Button
				onClick={goBack}
				startIcon={<ArrowBackIcon />}
				size="small"
				sx={{ color: "var(--white)", textTransform: "none" }}
			>
				Courses
			</Button>
			<TerrainIcon fontSize="small" />
			<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--white)" }}>
				Heat Map Editor
			</Typography>
			<Typography variant="body2" sx={{ opacity: 0.85, color: "var(--white)" }}>
				{step}m · {id_course}
			</Typography>
			{uploading && (
				<Box
					sx={{
						position: "absolute",
						left: "50%",
						top: 0,
						height: "100%",
						transform: "translateX(-50%)",
						display: "flex",
						alignItems: "center",
						gap: 1,
						pointerEvents: "none",
					}}
				>
					<CircularProgress size={16} sx={{ color: "var(--white)" }} />
					<Typography variant="body2" sx={{ color: "var(--white)" }}>
						Uploading edited terrain…
					</Typography>
				</Box>
			)}
			{lastModified && (
				<Typography
					variant="body2"
					sx={{ ml: "auto", fontWeight: 600, color: "var(--white)" }}
				>
					Last modified: {dateTimeFormat(lastModified)}
				</Typography>
			)}
		</Box>
	);

	if (status === "ready" && iframeKey) {
		return (
			<Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
				<TopBar />
				<iframe
					title="Terrain Editor"
					src={`/heatmap-tool/index.html?source=idb&key=${encodeURIComponent(
						iframeKey
					)}`}
					style={{ flex: "1 1 auto", width: "100%", border: "none", display: "block" }}
				/>
			</Box>
		);
	}

	return (
		<Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
			<TopBar />
			<Box
				sx={{
					flex: "1 1 auto",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 2,
					px: 3,
					textAlign: "center",
				}}
			>
				{status === "loading" ? (
					<>
						<CircularProgress />
						<Typography variant="body1" color="text.secondary">
							Loading {step}m terrain…
						</Typography>
					</>
				) : (
					<>
						<ErrorOutlineIcon color="error" sx={{ fontSize: 56 }} />
						<Typography variant="h6">Couldn't open the editor</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
							{message}
						</Typography>
						<Button
							variant="contained"
							onClick={goBack}
							sx={{
								mt: 1,
								bgcolor: "var(--blue)",
								"&:hover": { bgcolor: "var(--blue)" },
							}}
						>
							Back to courses
						</Button>
					</>
				)}
			</Box>
		</Box>
	);
};

export default HeatMapEditor;
